import React from 'react';
import PropTypes from 'prop-types';
import { authGetRequest } from 'helpers/apiRequests';
import { createLayer, clearMapLayer } from 'utils/map/layers';
import { hasObjectKeys, parseDate } from 'utils/main';
import {
  addLayerToMap,
  getAllClickedFeatures,
  getAllFeaturesByDetail,
  zoomToGeojsonGeometryWithBuffer,
  removeComponentLayers,
} from 'utils/map/map';
import useStyles from 'consts/styles/RightScreenPart';
import { useSelector } from 'react-redux';
import CustomizedTable from 'components/common/components/tablev2/CustomizedTable';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Icons from 'consts/icons';
import { userStyles } from 'consts/styles/User';
import { addMissionApprovals } from './utils/main';
import DisplayMissionHistoryDetails from './DisplayMissionHistoryDetails';

const MissionsHistory = props => {
  const {
    filtersDataObject,
    filtersDateTimeMissions,
    activeDetails,
    setActiveDetails,
    checkItem,
  } = props;
  const timezone = useSelector(state => state.timezone);
  const map = useSelector(state => state.map);
  const [approvals, setApprovals] = React.useState([]);
  const [idItemOnClick, setIdItemOnClick] = React.useState(null);
  const [totalRows, setTotalRows] = React.useState(0);
  const [selectedMission, setSelectedMission] = React.useState(null);

  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [currentPage, setCurrentPage] = React.useState(0);
  const layer = React.useRef(null);

  const subtitles = useSelector(state => state.language).missionsApprovals;
  const closeSubtitle = useSelector(state => state.language).close_;

  const classes = useStyles();
  const classesUser = userStyles();

  React.useEffect(() => {
    getApprovals();
    layer.current = createLayer('missionsLayer');
  }, []);

  const initMap = () => {
    if (!hasObjectKeys(map)) return;
    addLayerToMap(map, layer.current);
    map.on('click', handleClick);
    return () => cleanup();
  };

  const handleClick = event => {
    if (!event) return;
    setIdItemOnClick(
      getAllFeaturesByDetail(
        getAllClickedFeatures(map, event),
        'missionApproval'
      )[0]?.id_.substring(0, 36)
    );
  };

  const cleanup = () => {
    map.un('click', handleClick);
    removeComponentLayers(map);
  };

  React.useEffect(initMap, [map, checkItem]);

  React.useEffect(() => {
    if (layer.current !== null) addMissionApprovals(layer.current, approvals);
  }, [approvals]);

  React.useEffect(() => {
    if (selectedMission) {
      clearMapLayer(layer.current);
      addMissionApprovals(layer.current, [selectedMission]);
      setActiveDetails(false);
    } else {
      clearMapLayer(layer.current);
      addMissionApprovals(layer.current, approvals);
    }
  }, [selectedMission]);

  React.useEffect(() => {
    if (activeDetails !== false) setSelectedMission(null);
  }, [activeDetails]);

  const rowClicked = data => {
    setSelectedMission(approvals.find(mission => mission.uid === data.id));
  };

  React.useEffect(() => {
    if (idItemOnClick !== null) {
      setSelectedMission(
        approvals.find(mission => mission.uid === idItemOnClick)
      );
      return () => setIdItemOnClick(null);
    }
  }, [idItemOnClick]);

  const getApprovals = async (filtersObj = {}) => {
    const result = await authGetRequest('missionsFilters', {
      ...filtersObj,
      page: currentPage,
      page_size: rowsPerPage,
    });
    const resultLength = await authGetRequest(
      'missionsFiltersLength',
      filtersObj
    );
    if (result?.status === 200) {
      clearMapLayer(layer.current);
      setApprovals(result.data);
    }
    if (resultLength?.status === 200) setTotalRows(resultLength.data);
  };

  const handleZoom = () => {
    zoomToGeojsonGeometryWithBuffer(map, selectedMission.legs);
  };

  React.useEffect(() => {
    const filtersData = { ...filtersDataObject, ...filtersDateTimeMissions };
    getApprovals(filtersData);
  }, [filtersDataObject, filtersDateTimeMissions, rowsPerPage, currentPage]);

  return (
    <>
      <div className={`${classes.Wrapper}__history-wrapper`}>
        <CustomizedTable
          headers={[`${subtitles.uid_}`, 'Start', 'Stop', 'Status']}
          data={approvals.map(approval => ({
            id: approval.uid,
            start: parseDate(approval.start, timezone),
            stop: parseDate(approval.stop, timezone),
            status: approval.dtm_status,
          }))}
          onClickFunction={rowClicked}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={totalRows}
          selectedRow={selectedMission}
          idItemOnClick={idItemOnClick}
          filtersUrl="missionsFilters"
        />
      </div>
      {!activeDetails && selectedMission ? (
        <Grid
          container
          className={`${classesUser.User}__dashboard ${classesUser.User}`}
        >
          <Tooltip title={closeSubtitle} placement="bottom">
            <div
              className={`${classesUser.User}__settings__close`}
              onClick={() => setSelectedMission(null)}
              onKeyPress={() => setSelectedMission(null)}
              role="button"
              tabIndex={0}
            >
              <div
                className={`${classesUser.User}__settings__close__container`}
              >
                <Icons.Actions.Close />
              </div>
            </div>
          </Tooltip>
          <DisplayMissionHistoryDetails
            selectedMission={selectedMission}
            setSelectedMission={setSelectedMission}
            handleZoom={handleZoom}
          />
        </Grid>
      ) : null}
    </>
  );
};

MissionsHistory.propTypes = {
  filtersDataObject: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  filtersDateTimeMissions: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  activeDetails: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  setActiveDetails: PropTypes.func,
  checkItem: PropTypes.object,
};

export default MissionsHistory;
